import { useState, useEffect } from 'react';
import { Plus, X, Folder } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface FolderType {
  id: string;
  name: string;
  color?: string;
  icon?: string;
}

interface FolderSelectorProps {
  trigger: React.ReactNode;
  selectedFolders: FolderType[];
  availableFolders: FolderType[];
  organizationId: string;
  onFoldersChange: (folders: FolderType[]) => void;
  onFolderCreated?: () => void;
  align?: 'start' | 'center' | 'end';
}

export function FolderSelector({
  trigger,
  selectedFolders,
  availableFolders,
  organizationId,
  onFoldersChange,
  onFolderCreated,
  align = 'end',
}: FolderSelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [options, setOptions] = useState<FolderType[]>(availableFolders);
  const [isSearching, setIsSearching] = useState(false);

  const normalize = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
  const normalizedQuery = normalize(query);

  // Debounced search
  useEffect(() => {
    if (!open || !organizationId) return;
    
    const searchFolders = async () => {
      setIsSearching(true);
      try {
        let folderQuery = supabase
          .from('folders')
          .select('id, name, color, icon')
          .eq('organization_id', organizationId)
          .order('name');
        
        // Add search filter if query exists
        if (normalizedQuery) {
          folderQuery = folderQuery.ilike('name', `%${normalizedQuery}%`);
        }
        
        const { data } = await folderQuery.limit(100);
        if (data) setOptions(data);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(searchFolders, 300);
    return () => clearTimeout(timeoutId);
  }, [open, organizationId, normalizedQuery]);

  const toggleFolder = (folder: FolderType) => {
    const isSelected = selectedFolders.some(f => f.id === folder.id);
    if (isSelected) {
      onFoldersChange(selectedFolders.filter(f => f.id !== folder.id));
    } else {
      onFoldersChange([...selectedFolders, folder]);
    }
  };

  const createNewFolder = async () => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery || !organizationId) return;

    setIsCreating(true);
    try {
      const { data, error } = await supabase
        .from('folders')
        .insert({
          name: trimmedQuery,
          organization_id: organizationId,
          color: `hsl(${Math.random() * 360}, 70%, 50%)`,
          icon: 'folder',
        })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        const newFolder = { 
          id: data.id, 
          name: data.name, 
          color: data.color,
          icon: data.icon 
        } as FolderType;
        onFoldersChange([...selectedFolders, newFolder]);
        setOptions(prev => (prev.some(f => f.id === newFolder.id) ? prev : [...prev, newFolder]));
        setQuery('');
        toast.success(`Folder "${trimmedQuery}" created`);
        onFolderCreated?.();
      }
    } catch (error: any) {
      console.error('Error creating folder:', error);
      if (error?.code === '23505') {
        // Folder already exists in this organization, select it instead
        const { data: existing } = await supabase
          .from('folders')
          .select('id, name, color, icon')
          .eq('organization_id', organizationId)
          .eq('name', trimmedQuery)
          .maybeSingle();
        if (existing) {
          const existingFolder = { 
            id: existing.id, 
            name: existing.name, 
            color: (existing as any).color,
            icon: (existing as any).icon 
          } as FolderType;
          onFoldersChange([...selectedFolders.filter(f => f.id !== existingFolder.id), existingFolder]);
          setOptions(prev => (prev.some(f => f.id === existingFolder.id) ? prev : [...prev, existingFolder]));
          setQuery('');
          toast.success(`Folder "${trimmedQuery}" added`);
          onFolderCreated?.();
        } else {
          toast.info('Folder already exists');
        }
      } else {
        toast.error(error.message || 'Failed to create folder');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const filteredFolders = options;

  const exactMatch = options.find(folder => normalize(folder.name) === normalizedQuery);
  const showCreateOption = normalizedQuery && !exactMatch;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger}
      </PopoverTrigger>
      <PopoverContent className="z-50 bg-popover shadow-md w-80 p-0" align={align}>
        <div className="flex flex-col">
          <div className="p-3 border-b">
            <h4 className="font-medium text-sm">Folders</h4>
            {selectedFolders.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {selectedFolders.map((folder) => {
                  const baseColor = folder.color || 'hsl(var(--primary))';
                  const transparentBg = baseColor.startsWith('hsl')
                    ? baseColor.replace(')', ' / 0.2)')
                    : baseColor + '33';
                  const borderColor = baseColor;
                  
                  return (
                    <div
                      key={folder.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border-2"
                      style={{ 
                        backgroundColor: transparentBg,
                        borderColor: borderColor,
                        color: 'white'
                      }}
                    >
                      <Folder className="w-3.5 h-3.5" />
                      <span>{folder.name}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onFoldersChange(selectedFolders.filter(f => f.id !== folder.id));
                        }}
                        className="opacity-70 hover:opacity-100 transition-fast"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search or create folders"
              className="h-9"
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              {showCreateOption && (
                <CommandGroup>
                  <CommandItem
                    onSelect={createNewFolder}
                    disabled={isCreating}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create "{query.trim()}"
                  </CommandItem>
                </CommandGroup>
              )}
              <CommandEmpty>No folders found.</CommandEmpty>
              <CommandGroup>
                {filteredFolders.map((folder) => (
                  <CommandItem
                    key={folder.id}
                    value={normalize(folder.name)}
                    onSelect={() => toggleFolder(folder)}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      {folder.color && (
                        <div
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: folder.color }}
                        />
                      )}
                      <Folder className="w-4 h-4" />
                      {folder.name.trim()}
                    </div>
                    <div
                      className={cn(
                        "ml-auto h-4 w-4 rounded-sm border border-primary",
                        selectedFolders.some(f => f.id === folder.id)
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50"
                      )}
                    >
                      {selectedFolders.some(f => f.id === folder.id) && (
                        <X className="h-3 w-3" />
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      </PopoverContent>
    </Popover>
  );
}
